const NOTIF_PREFS_STORAGE_KEY_PREFIX = 'employeeNotificationSettings:v1';

export const DEFAULT_NOTIFICATION_SETTINGS = {
  'new-job-assigned': true,
  'job-cancelled': true,
  'schedule-changed': true,
  'urgent-project': true,
  'clock-in-reminder': true,
  'shift-reminder': true,
  'timesheet-submission-reminder': false,
  'report-submission-reminder': true,
  'leave-reminder': true,
  'work-report-submission': true,
  'pipeline-files': true,
  'new-team-member': true,
  'workforce-document-added': true,
  'document-expiry-alert': true,
  'low-stock-alert': true,
  'goods-damaged': true,
  'product-expiry-alert': true,
  'payment-received': true,
  'invoice-generated': true,
  'payment-pending': true,
  'quotation-accepted': true,
  'quotation-rejected': true,
  'contract-accepted': true,
  'contract-rejected': true,
  'proposal-accepted': true,
  'proposal-rejected': true,
  'new-chat-message': true,
  'manager-mention': true,
  'support-message': false,
  'notes-communication': true,
};

function safeJsonParse(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function getNotificationSettingsStorageKey(userId, portal) {
  const safeUserId = String(userId || 'unknown');
  const safePortal = String(portal || 'cleaner').toLowerCase();
  return `${NOTIF_PREFS_STORAGE_KEY_PREFIX}:${safePortal}:${safeUserId}`;
}

export function readNotificationSettings(userId, portal, defaultSettings = {}) {
  const key = getNotificationSettingsStorageKey(userId, portal);
  const raw = localStorage.getItem(key);
  const parsed = safeJsonParse(raw, null);
  if (!parsed || typeof parsed !== 'object') return { ...defaultSettings };
  return { ...defaultSettings, ...parsed };
}

export function writeNotificationSettings(userId, portal, settings) {
  const key = getNotificationSettingsStorageKey(userId, portal);
  localStorage.setItem(key, JSON.stringify(settings || {}));
}

export function getAnnouncementPreferenceKey(event = {}) {
  const title = String(event?.title || '').toLowerCase();
  const message = String(event?.message || '').toLowerCase();
  const combined = `${title} ${message}`;
  if (combined.includes('new job assigned')) return 'new-job-assigned';
  if (combined.includes('job cancelled') || combined.includes('job canceled')) {
    return 'job-cancelled';
  }
  if (combined.includes('schedule changed')) return 'schedule-changed';
  if (combined.includes('urgent project')) return 'urgent-project';
  if (combined.includes('leave request')) return 'leave-reminder';
  if (combined.includes('work report') && combined.includes('submitted')) {
    return 'work-report-submission';
  }
  if (combined.includes('low stock') || combined.includes('out of stock')) {
    return 'low-stock-alert';
  }
  if (combined.includes('goods damaged') || combined.includes('reported as damaged')) {
    return 'goods-damaged';
  }
  if (combined.includes('nearing expiry') || combined.includes('products nearing expiry')) {
    return 'product-expiry-alert';
  }
  if (
    combined.includes('expiry reminder') ||
    combined.includes('please renew') ||
    (combined.includes('expired') && combined.includes('renew'))
  ) {
    return 'document-expiry-alert';
  }
  if (combined.includes('payslip')) return 'invoice-generated';
  if (combined.includes('invoice')) return 'invoice-generated';
  if (combined.includes('quotation accepted')) return 'quotation-accepted';
  if (
    combined.includes('quotation rejected') ||
    combined.includes('quotation declined') ||
    combined.includes('quotation change requested')
  ) {
    return 'quotation-rejected';
  }
  if (
    combined.includes('contract signed') ||
    combined.includes('contract accepted')
  ) {
    return 'contract-accepted';
  }
  if (
    combined.includes('contract declined') ||
    combined.includes('contract change requested')
  ) {
    return 'contract-rejected';
  }
  if (combined.includes('proposal accepted')) return 'proposal-accepted';
  if (
    combined.includes('proposal declined') ||
    combined.includes('proposal change requested')
  ) {
    return 'proposal-rejected';
  }
  if (combined.includes('pending') && combined.includes('payment')) return 'payment-pending';
  if (
    combined.includes('payment received') ||
    combined.includes('payment deposited') ||
    (combined.includes('payment') && combined.includes('received'))
  ) {
    return 'payment-received';
  }
  if (combined.includes('support')) return 'support-message';
  if (
    combined.includes('clock-in reminder') ||
    (combined.includes('clock in') && combined.includes('time clock'))
  ) {
    return 'clock-in-reminder';
  }
  if (
    combined.includes('internal note on reported issue') ||
    (combined.includes('reported issue') && combined.includes('internal note'))
  ) {
    return 'notes-communication';
  }
  if (combined.includes('new team member') || combined.includes('workforce id:')) {
    return 'new-team-member';
  }
  if (combined.includes('new workforce document')) {
    return 'workforce-document-added';
  }
  if (combined.includes('approved work report') || combined.includes('new lead file')) {
    return 'pipeline-files';
  }
  return null;
}

/**
 * Admin app: map notification to dashboard `activePage` query or a top-level subpath.
 */
export function getAdminNotificationDestination(item) {
  if (item?.type === 'announcement') {
    const prefKey = getAnnouncementPreferenceKey({
      title: item?.title,
      message: item?.content,
    });

    switch (prefKey) {
      case 'payment-received':
      case 'invoice-generated':
      case 'payment-pending':
        return { route: '', searchParams: { page: 'employee-payroll' } };
      case 'quotation-accepted':
      case 'quotation-rejected':
        return { route: '', searchParams: { page: 'customer' } };
      case 'contract-accepted':
      case 'contract-rejected':
        return { route: '', searchParams: { page: 'Contract' } };
      case 'proposal-accepted':
      case 'proposal-rejected':
        return { route: '', searchParams: { page: 'proposals' } };
      case 'low-stock-alert':
      case 'goods-damaged':
      case 'product-expiry-alert':
        return { route: '', searchParams: { page: 'inventory-update' } };
      case 'new-job-assigned':
      case 'job-cancelled':
      case 'schedule-changed':
      case 'urgent-project':
        return { route: '', searchParams: { page: 'project' } };
      case 'leave-reminder':
        return { route: '', searchParams: { page: 'leave' } };
      case 'work-report-submission':
        return { route: '', searchParams: { page: 'timesheet' } };
      case 'support-message':
        return { route: '/help-support', searchParams: {} };
      case 'notes-communication':
        return {
          route: 'support-management/reported-issues',
          searchParams: {},
        };
      case 'clock-in-reminder':
        return { route: '', searchParams: {} };
      case 'new-team-member':
        return { route: '', searchParams: { page: 'team-member' } };
      case 'workforce-document-added':
        return { route: '', searchParams: { page: 'document-workforce' } };
      case 'document-expiry-alert':
        return { route: '', searchParams: { page: 'document-workforce' } };
      case 'pipeline-files':
        return { route: '', searchParams: {} };
      default:
        return { route: '', searchParams: { page: 'chat', tab: 'announcements' } };
    }
  }

  const searchParams = { page: 'chat' };
  if (item?.conversationId) searchParams.conversationId = item.conversationId;
  if (item?.senderId) searchParams.contactId = item.senderId;
  return { route: '', searchParams };
}

export function buildAdminNotificationUrl(destination) {
  const { route, searchParams = {} } = destination;
  const qs = new URLSearchParams();
  Object.entries(searchParams).forEach(([k, v]) => {
    if (v != null && String(v).length > 0) {
      qs.set(k, String(v));
    }
  });
  const qsStr = qs.toString();
  if (!route) {
    return qsStr ? `/?${qsStr}` : '/';
  }
  const path = route.startsWith('/') ? route : `/${route}`;
  return qsStr ? `${path}?${qsStr}` : path;
}
